import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map } from 'rxjs';
import { SalesChannelDialog } from '../dialogs/sales-channel-dialog/sales-channel.dialog';
import { InventoryItemFactory } from '../factories/inventory-item.factory';
import { InventoryItem, InventoryItemFeature } from '@contracts/interfaces/inventory-item.interface';
import { ConnectionService } from './connection.service';
import { InventoryItemDialogComponent } from '../dialogs/inventory-item-dialog/inventory-item.dialog';
import { InventoryItemFeatureDialogComponent } from '../dialogs/inventory-item-feature/inventory-item-feature.dialog';
import { InventoryCategory } from '@contracts/interfaces/inventory-category';
import { InventoryCategoryFactory } from '../factories/inventory-category.factory';

@Injectable({
  providedIn: 'root'
})
export class InventoryService {
 
  public itemUpdatedEvent = new EventEmitter();

  constructor(private http: HttpClient,
    private connectionService: ConnectionService,
    public dialog: MatDialog) {}

  private get sessionOptions() {
    return { headers: { authtoken: this.connectionService.activeConnection?.token || '' } };
  }

  get(itemNumber: number) {
    return this.http.post(this.connectionService.address + "/inventory-service/get-item", { itemNumber }, this.sessionOptions).pipe(
      map((r: InventoryItem) => {
        if (r != null) {
          return InventoryItemFactory.validate(r);
        }
      })
    );
  }

  search(query: string, categoryIndex: number) {
    return this.http.post(this.connectionService.address + "/inventory-service/search", { query, categoryIndex }).pipe(
      map((items: InventoryItem[]) => {
        for (let i=0;i<items.length;i++) {
          items[i] = InventoryItemFactory.validate(items[i]);
        }
        return items;
      })
    );
  }

  isListed(item: InventoryItem) {
    if (item.webflow.isListed) {
      return true;
    }
    if (item.facebook.url != "") {
      return true;
    }

    if (item.ebay.ItemID != 0) {
      return true;
    }

    return false;    
  }
  
  find(query: any = {}, sortBy: string, sortDir: string, limit: number = null) {
    return this.http.post(this.connectionService.address + "/inventory-service/find", { query, sortBy, sortDir, limit }).pipe(
      map((items: InventoryItem[]) => {
        for (let i=0;i<items.length;i++) {
          items[i] = InventoryItemFactory.validate(items[i]);
        }
        return items;
      })
    );
  }

  find2(query: any = {}, sortBy: string, sortDir: string) {
    return this.http.post(this.connectionService.address + "/inventory-service/find2", { query, sortBy, sortDir }).pipe(
      map((items: InventoryItem[]) => {
        for (let i=0;i<items.length;i++) {
          items[i] = InventoryItemFactory.validate(items[i]);
        }
        return items;
      })
    );
  }

  getInventoryStats() {
    return this.http.post(this.connectionService.address + "/inventory-service/get-stats", {});
  }

  create(item: InventoryItem) {
    return this.http.post(this.connectionService.address + "/inventory-service/create-item", { item }, this.sessionOptions);
  }

  updateOne(item: InventoryItem) {
    if (item.status == 'empty') {
      item.status = '';
    }
    this.itemUpdatedEvent.emit(item);
    return this.http.post(this.connectionService.address + "/inventory-service/update-item", { item }, this.sessionOptions);
  }

  deleteOne(item: InventoryItem) {
    return this.http.post(this.connectionService.address + "/inventory-service/delete-item", { item });
  }

  getComponentDef(type: string) {
    return this.http.post(this.connectionService.address + "/inventory-service/get-component-def", { type });
  }

  getItemType(type: string) {
    return this.http.post(this.connectionService.address + "/inventory-service/get-item-type", { type });
  }

  getItemTypes(type: string = null) {
    return this.http.post(this.connectionService.address + "/inventory-service/get-item-types", { type });
  }

  createItemTemplate(template: any) {
    return this.http.post(this.connectionService.address + "/inventory-service/create-item-template", { template });
  }

  updateItemTemplate(template: any) {
    return this.http.post(this.connectionService.address + "/inventory-service/update-item-template", { template });
  }

  deleteItemTemplate(template: any) {
    return this.http.post(this.connectionService.address + "/inventory-service/delete-item-template", { template });
  }

  getInventoryListCompiled() {
    return this.http.post(this.connectionService.address + "/inventory-service/inventory-list-compiled", { }).pipe(
      map((categories: InventoryCategory[]) => {
        for (let i=0;i<categories.length;i++) {
          categories[i] = InventoryCategoryFactory.validate(categories[i]);
        }
        return categories;
      })
    );
  }

  getMediaList(item: InventoryItem) {
    return this.http.post(this.connectionService.address + "/inventory-service/get-item-media-list", { item });    
  }

  openSalesChannelDialog(item: InventoryItem, salesChannel: any, callback: any) {

    let dialog = this.dialog.open(SalesChannelDialog, {
      width: '800px',
      autoFocus: false,
      data: { 
        item, 
        salesChannel
      }
    });

    dialog.afterClosed().subscribe((r: any) => {
      callback(r);
    })
  }

  openInventoryItemDialog(item: InventoryItem, list: number[], currentIndex: number, callback: any) {

    let dialog = this.dialog.open(InventoryItemDialogComponent, {
      panelClass: "dialog-test",
      autoFocus: false,
      data: {
        item,
        list,
        currentIndex
      }
    });

    dialog.afterClosed().subscribe((r: any) => {
      callback(r);
    })
  }

  toggleCheckout(item: InventoryItem) {
    const checkedOut = item.status !== 'checked-out';
    item.status = checkedOut ? 'checked-out' : 'Available';
    item.checkOutReason = checkedOut ? 'Sold' : '';
    item.lastModified = new Date();
    item.log = item.log || [];
    item.log.push({
      text: checkedOut ? 'Checked out' : 'Checked in',
      date: new Date()
    });
    return this.updateOne(item);
  }

  openItemFeatureDialog(itemFeature: any, itemFeatureValue: string, callback: any) {
    let dialog = this.dialog.open(InventoryItemFeatureDialogComponent, {
      width: "100%",
      maxWidth: "100%",
      position: {
        top: '20px'
      },
      autoFocus: true,
      data: {
        itemFeature,
        itemFeatureValue
      }
    });

    dialog.afterClosed().subscribe((r: any) => {
      callback(r);
    })
  }
}
